import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Typography, Space } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { getApiErrorMessage } from '../../utils/apiErrors';
import {
  formatMfaResendCooldown,
  remainingCooldownSeconds,
} from '../../utils/mfaEmailHints';

export type EmailMfaStep = 'enroll' | 'challenge';

export interface EmailMfaChallengeFormProps {
  mfaStep: EmailMfaStep;
  loading?: boolean;
  resendLoading?: boolean;
  /** Static status text (sent / send failed / errors). Cooldown uses resendCooldownSeconds. */
  resendHint?: string | null;
  /**
   * When set to a positive value, starts a live countdown and disables Resend
   * until it reaches 0. Change resendCooldownToken (or the seconds value) to restart.
   */
  resendCooldownSeconds?: number | null;
  /** Bump when the same cooldown length must restart (e.g. another early resend). */
  resendCooldownToken?: number | string | null;
  /** Fired once when a live countdown reaches 0 so parents can clear cooldown state. */
  onResendCooldownFinished?: () => void;
  onVerify: (otp: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  backLabel?: string;
}

/**
 * Shared email OTP challenge UI for LoginPage and LoginDialog (ISSUE-263).
 * Separate Form instance — does not share state with the password form (§8 multi-step).
 */
export const EmailMfaChallengeForm: React.FC<EmailMfaChallengeFormProps> = ({
  mfaStep,
  loading = false,
  resendLoading = false,
  resendHint = null,
  resendCooldownSeconds = null,
  resendCooldownToken = null,
  onResendCooldownFinished,
  onVerify,
  onResend,
  onBack,
  backLabel = 'Back to sign in',
}) => {
  const [otpForm] = Form.useForm();
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    const start = resendCooldownSeconds ?? 0;
    if (start <= 0) {
      setSecondsLeft(0);
      return;
    }
    const startedAt = Date.now();
    setSecondsLeft(remainingCooldownSeconds(start, startedAt, Date.now()));
    let finishedNotified = false;
    const id = window.setInterval(() => {
      const left = remainingCooldownSeconds(start, startedAt, Date.now());
      setSecondsLeft(left);
      if (left <= 0) {
        window.clearInterval(id);
        if (!finishedNotified) {
          finishedNotified = true;
          onResendCooldownFinished?.();
        }
      }
    }, 250);
    return () => window.clearInterval(id);
    // onResendCooldownFinished is intentionally omitted: parents pass inline lambdas;
    // including it would restart the timer every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cooldown identity is seconds+token
  }, [resendCooldownSeconds, resendCooldownToken]);

  const handleFinish = async (values: { otp: string }) => {
    try {
      await onVerify(values.otp.trim());
    } catch (err: unknown) {
      otpForm.setFields([
        {
          name: 'otp',
          errors: [getApiErrorMessage(err, 'Invalid or expired verification code.')],
        },
      ]);
    }
  };

  const cooldownActive = secondsLeft > 0;

  return (
    <>
      <Typography.Paragraph style={{ marginBottom: 16 }}>
        {mfaStep === 'enroll'
          ? 'Enter the 6-digit code we emailed you to finish setting up sign-in verification. Check your inbox and spam folder.'
          : 'Enter the 6-digit code we emailed you to continue. Check your inbox and spam folder.'}
      </Typography.Paragraph>
      <Form form={otpForm} layout="vertical" onFinish={handleFinish} autoComplete="off">
        <Form.Item
          name="otp"
          label="Verification code"
          rules={[
            { required: true, message: 'Enter the code from your email' },
            { pattern: /^\d{6}$/, message: 'Enter the 6-digit code' },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="123456"
            size="large"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block size="large">
            Verify and continue
          </Button>
        </Form.Item>
      </Form>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Button
          type="link"
          onClick={() => void onResend()}
          loading={resendLoading}
          disabled={cooldownActive || resendLoading}
          style={{ padding: 0 }}
        >
          {cooldownActive ? `Resend code (${secondsLeft}s)` : 'Resend code'}
        </Button>
        {resendHint && (
          <Typography.Text type="secondary" data-testid="mfa-resend-hint">
            {resendHint}
          </Typography.Text>
        )}
        {cooldownActive && (
          <Typography.Text type="secondary" data-testid="mfa-resend-status">
            {formatMfaResendCooldown(secondsLeft)}
          </Typography.Text>
        )}
        <Button
          type="link"
          onClick={() => {
            otpForm.resetFields();
            onBack();
          }}
          style={{ padding: 0 }}
        >
          {backLabel}
        </Button>
      </Space>
    </>
  );
};
